import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import Input from '@visa/vds-react/input-styled';
import Textarea from '@visa/vds-react/textarea-styled';

import CybsIcon from '@cybs-ui/components/lib/icon';
import CybsButton from '@cybs-ui/components/lib/button';
import CybsScreenReaderManager from '@cybs-ui/components/lib/screen-reader-manager';
import stylesBuilder from './component.styles';

/**
 * Some props are disabled so that they are not spread in VDS without the i18n object.
 */
const CybsTextfield = React.forwardRef(
  (
    {
      activeButton,
      autoResize,
      colorScheme,
      containerProps,
      contentBeforeInput,
      customCSS,
      dataTest,
      defaultValue,
      errorText, // eslint-disable-line
      helpText, // eslint-disable-line
      i18n: i18nProp,
      iconClass,
      iconType,
      // Prevent selected from being spread to VDS-React and use isInvalid instead
      invalid, // eslint-disable-line
      isInvalid,
      isMultiline,
      label, // eslint-disable-line
      maxCharacters,
      textAreaChange,
      onChange,
      onIconClick,
      onBlur,
      onFocus,
      onIconKeyDown,
      prenventIconFocus,
      readOnly,
      resolution,
      required,
      rows,
      showCheckmark,
      showCounter,
      showErrorText,
      value: valueProp,
      width,
      hideIconLabel,
      useNativePlaceholder,
      ...props
    },
    ref
  ) => {
    // Use defaults for undefined i18n strings
    const i18n = {
      ...CybsTextfield.defaultProps.i18n,
      ...i18nProp
    };
    const { disabled } = props;

    const { containerStyles, inputStyles } = stylesBuilder({
      contentBeforeInput,
      readOnly,
      required,
      width
    });

    const wrapperInputProps = {
      'data-test': `${dataTest}-cybsTextField`,
      ...containerProps
    };
    const wrapperTextareaProps = {
      'data-test': `${dataTest}-cybsTextArea`,
      ...containerProps
    };
    const onTextareaChange = e => {
      if (maxCharacters && e.target.value.length >= maxCharacters) {
        e.target.value = e.target.value.substring(0, maxCharacters);
      }
      if (typeof textAreaChange === 'function') {
        textAreaChange(e);
      }
    };

    const handleIconClick = e => {
      e.preventDefault();
      e.stopPropagation();
      onIconClick(e);
    };

    let icon;
    const [value, setValue] = useState(valueProp || defaultValue);

    useEffect(() => {
      setValue(valueProp);
    }, [valueProp, iconClass]);

    // Logic to read error state on blur
    const [justBlurred, setJustBlurred] = useState({});

    useEffect(() => {
      if (justBlurred && isInvalid && i18n.errorText) {
        CybsScreenReaderManager.addNotification(i18n.errorText);
      }
      setJustBlurred(false);
    }, [justBlurred]);

    const handleChange = event => {
      setValue(event.target.value);
      if (typeof onChange === 'function') {
        onChange(event);
      }
    };

    // Only used in multiLine
    const handleFocus = event => {
      CybsScreenReaderManager.addNotification(i18n.errorText);
      if (onFocus === 'function') {
        onFocus(event);
      }
    };
    const commonProps = {
      prenventIconFocus,
      colorScheme,
      dataTest,
      disabled: readOnly && !activeButton,
      i18n: { ...i18n, ariaLabel: i18n.buttonAriaLabel },
      iconType,
      onClick: handleIconClick,
      onKeyDown: onIconKeyDown,
      readOnly,
      resolution,
      variant: 'icon',
      tabIndex: !activeButton && prenventIconFocus && '-1'
    };
    if (iconClass !== 'clear' && iconClass) {
      icon = (
        <CybsButton
          iconClass={iconClass}
          aria-hidden={hideIconLabel || undefined}
          {...commonProps}
          tooltipShowOn={hideIconLabel ? 'none' : undefined}
        />
      );
    } else if (iconClass === 'clear') {
      commonProps.i18n.label =
        i18n.clearLabel ?? `Clear ${i18n.label ?? 'field'}`;

      icon =
        value !== '' ? <CybsButton iconClass="clear" {...commonProps} /> : null;
    }

    const textfieldRef = useRef(null);

    const handleBlur = e => {
      const el =
        textfieldRef && textfieldRef.current ? textfieldRef.current : null;

      const flatChildren = [];
      if (el) {
        const _createFlatChildren = child => {
          if (child && child.childNodes.length > 0) {
            for (let i = 0; i < child.childNodes.length; i++) {
              const currNode = child.childNodes[i];

              if (currNode && !(currNode.childNodes.length > 0)) {
                flatChildren.push(currNode);
              } else {
                flatChildren.push(currNode);
                _createFlatChildren(currNode);
              }
            }
          } else {
            flatChildren.push(child);
          }
        };

        _createFlatChildren(el);
      }
      let isContained = false;
      const relatedTarget = e && e.relatedTarget ? e.relatedTarget : null;
      // map through set and see if relatedTarget exists
      if (flatChildren && flatChildren.length > 0 && relatedTarget) {
        for (let i = 0; i < flatChildren.length; i++) {
          const currNode = flatChildren[i];
          if (currNode === relatedTarget) {
            isContained = true;
            break;
          }
        }
      }

      if (isContained) {
        e.preventDefault();
      } else {
        setJustBlurred(true);
        onBlur(e);
      }
    };

    const inputPlaceholder =
      disabled || readOnly ? undefined : i18n.placeholder;

    return isMultiline ? (
      <Textarea
        aria-live="polite"
        autoResize={autoResize}
        containerProps={{ ...wrapperTextareaProps }}
        customCSS={theme => [inputStyles(theme), customCSS(theme)]}
        errorIcon={<CybsIcon dataTest="error" name="error" resolution="tiny" />}
        errorText={i18n.errorText}
        helpText={i18n.helpText}
        invalid={Boolean(isInvalid)}
        label={i18n.label}
        maxCharacters={maxCharacters}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onInput={e => onTextareaChange(e)}
        readOnly={readOnly}
        ref={ref}
        rows={rows}
        required={required}
        showCounter={showCounter}
        showErrorText={Boolean(i18n.errorText)}
        showHelpText={Boolean(i18n.helpText)}
        value={value}
        {...props}
        placeholder={!value && inputPlaceholder}
      />
    ) : (
      <div ref={textfieldRef} css={containerStyles}>
        <Input
          contentBeforeInput={
            contentBeforeInput && (
              <div className="contentBeforeInput">{contentBeforeInput}</div>
            )
          }
          containerProps={{ ...wrapperInputProps }}
          customCSS={theme => [inputStyles(theme), customCSS(theme)]}
          errorIcon={
            <CybsIcon dataTest="error" name="error" resolution="tiny" />
          }
          errorText={i18n.errorText}
          helpText={i18n.helpText}
          invalid={Boolean(isInvalid)}
          label={i18n.label}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={onFocus}
          readOnly={readOnly}
          ref={ref}
          required={required}
          showCheckmark={showCheckmark}
          showErrorText={showErrorText && Boolean(i18n.errorText)}
          showHelpText={Boolean(i18n.helpText)}
          value={value}
          ariaLabel={i18n.ariaLabel || i18n.label || i18n.placeholder}
          useNativePlaceholder={useNativePlaceholder}
          {...props}
          placeholder={inputPlaceholder}
        >
          {icon}
        </Input>
      </div>
    );
  }
);

CybsTextfield.propTypes = {
  /**
   * call back function for the textarea chnage
   */
  textAreaChange: PropTypes.func,
  /**
   * If true, the text area will auto resize.
   */
  autoResize: PropTypes.bool,

  /**
   * colorScheme for Icon
   */
  colorScheme: PropTypes.oneOf(['default', 'primary', 'secondary', 'tertiary']),

  /**
   * Props to pass to element that wrap the label, the input/text area and the help text.
   */
  // eslint-disable-next-line react/forbid-prop-types
  containerProps: PropTypes.object,

  /**
   * Content Before input use for icons, etc
   */
  contentBeforeInput: PropTypes.node,

  /**
   *  @ignore
   */
  // eslint-disable-next-line react/require-default-props
  counter: PropTypes.func,

  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,

  /**
   * String with datatest name information
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * The default value input if the component is uncontrolled.
   * @deprecated
   */
  defaultValue: PropTypes.string,

  /**
   * If true, the input will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * @ignore
   * Indicates if the button should be active (even in read only mode)
   */
  activeButton: PropTypes.bool,

  /**
   *  i18n
   */
  i18n: PropTypes.shape({
    /**
     * A11y aria label for the icon.
     */
    buttonAriaLabel: PropTypes.string,

    /**
     * Error text for the input field
     */
    errorText: PropTypes.string,

    /**
     * Text displayed under the input field.
     */
    helpText: PropTypes.string,

    /**
     * Label for the input field.
     */
    label: PropTypes.string,

    /**
     * Placeholder in the input field.
     */
    placeholder: PropTypes.string,

    /**
     * ariaLabel in the input field.
     */
    ariaLabel: PropTypes.string,

    /**
     * Clear button's label.
     *
     * If left undefined, the component will use `Clear ${i18n.label ?? 'field'}`
     */
    clearLabel: PropTypes.string
  }),

  /**
   * Icon name
   */
  iconClass: PropTypes.string,

  /**
   * Icon type
   *
   */
  iconType: PropTypes.string,

  /**
   *  if `true` shows errorText
   */
  isInvalid: PropTypes.bool,

  /**
   * The maximum number of characters that the user can enter.
   * If this value isn't specified, the user can enter an unlimited number of characters.
   */
  maxCharacters: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   *If true, input base is a textarea.
   */
  isMultiline: PropTypes.bool,

  /**
   * Disable native HTML5 validation e.g. type=email, pattern="some regex pattern"
   */
  noValidate: PropTypes.bool,

  /**
   * Callback fired when the input element loses focus.
   */
  onBlur: PropTypes.func,

  /**
   * Callback fired when the value is changed.
   */
  onChange: PropTypes.func,

  /**
   * Callback fired when the input element is focused on.
   */
  onFocus: PropTypes.func,

  /**
   * Callback fired when the button icon is clicked.
   */
  onIconClick: PropTypes.func,

  /**
   * Callback fired when the button receives a key down event.
   */
  onIconKeyDown: PropTypes.func,

  /**

  /**
   * It prevents the user from changing the value of the field
   */
  readOnly: PropTypes.bool,

  /**
   * @ignore
   */
  required: PropTypes.bool,

  /**
   * resolution
   */
  resolution: PropTypes.string,

  /**
   * Number of rows to display when multiline option is set to true.
   */
  rows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Prevent focus on the icon button.
   */
  prenventIconFocus: PropTypes.bool,

  /**
   * Show positive checkmark validation if input value is valid.
   */
  showCheckmark: PropTypes.bool,

  /**
   * Show the number of characters.
   */
  showCounter: PropTypes.bool,

  /**
   * If `true` the input turns red and the error mesage is visible.
   */
  showErrorText: PropTypes.bool,

  /**
   * Type of the input element. It should be a valid HTML5 input type.
   */
  type: PropTypes.string,

  /**
   * The input element value
   */
  value: PropTypes.string,

  /**

  /**
   * Custom width for the input
   */
  width: PropTypes.string,
  /**
   * Hide icon aria label
   */
  hideIconLabel: PropTypes.bool,
  /**
   * Use the native placeholder attribute
   */
  useNativePlaceholder: PropTypes.bool
};

CybsTextfield.defaultProps = {
  textAreaChange: () => {},
  autoResize: false,
  activeButton: false,
  colorScheme: 'secondary',
  containerProps: null,
  contentBeforeInput: undefined,
  customCSS: () => '',
  defaultValue: undefined,
  disabled: false,
  i18n: {
    buttonAriaLabel: '',
    clearLabel: undefined,
    errorText: '',
    helpText: '',
    label: '',
    placeholder: '',
    ariaLabel: ''
  },
  iconClass: '',
  iconType: 'light-tiny',
  isInvalid: false,
  isMultiline: false,
  maxCharacters: undefined,
  prenventIconFocus: true,
  noValidate: true,
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  onIconClick: () => {},
  onIconKeyDown: () => {},
  readOnly: false,
  required: false,
  resolution: 'low',
  rows: 2,
  showCheckmark: false,
  showCounter: false,
  showErrorText: true,
  type: 'text',
  value: undefined,
  width: '',
  hideIconLabel: false,
  useNativePlaceholder: true
};

CybsTextfield.displayName = 'CybsTextfield';

export default CybsTextfield;
